// Utility functions for formatting data - ENHANCED: Smart timezone system
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { es, enUS as en } from 'date-fns/locale';

// ========================================
// 🌐 SMART TIMEZONE SYSTEM
// ========================================

// Cache para zonas horarias y datos del usuario
let timezonesCache = null;
let userTimezoneCache = null;
let browserTimezone = null;

/**
 * Cache para evitar cargas repetidas del JSON de zonas horarias
 */
const loadTimezonesData = async () => {
  if (timezonesCache) {
    return timezonesCache;
  }

  try {
    const response = await fetch('/countries-master/json/timezones.json');
    if (!response.ok) {
      throw new Error('Failed to load timezones');
    }

    timezonesCache = await response.json();
    return timezonesCache;
  } catch (error) {
    console.error('❌ Error loading timezones:', error);
    // Fallback basic data
    timezonesCache = [
      { value: 'America/Mexico_City', utc: ['America/Mexico_City'] },
      { value: 'America/New_York', utc: ['America/New_York'] },
      { value: 'America/Los_Angeles', utc: ['America/Los_Angeles'] },
      { value: 'UTC', utc: ['UTC'] }
    ];
    return timezonesCache;
  }
};

/**
 * Validar si una zona horaria es válida usando el JSON y el navegador
 */
const isValidTimezone = async (timezone) => {
  if (!timezone || typeof timezone !== 'string') {
    return false;
  }

  // Primero validar con el navegador (más rápido y confiable)
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
  } catch {
    return false;
  }

  // Luego verificar que esté en nuestro JSON (opcional, para consistencia)
  try {
    const timezonesData = await loadTimezonesData();
    const found = timezonesData.some(tz =>
      tz.utc && tz.utc.includes(timezone)
    );

    if (!found) {
      console.warn(`⚠️ Timezone ${timezone} is valid but not in our JSON data`);
    }

    return true; // Si es válido para el navegador, lo aceptamos
  } catch (error) {
    console.warn('⚠️ Could not validate timezone against JSON, but browser accepts it:', timezone);
    return true; // Si no podemos cargar el JSON pero el navegador lo acepta, lo usamos
  }
};

/**
 * Obtener zona horaria del navegador
 */
const getBrowserTimezone = () => {
  if (browserTimezone) {
    return browserTimezone;
  }

  try {
    browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return browserTimezone;
  } catch {
    browserTimezone = 'America/Mexico_City'; // Fallback seguro
    console.warn('⚠️ Could not detect browser timezone, using fallback:', browserTimezone);
    return browserTimezone;
  }
};

/**
 * Determinar la zona horaria efectiva del usuario
 * Prioridad: Usuario configurado (válido) > Navegador > Fallback
 */
export const getUserEffectiveTimezone = async (userTimezone = null) => {
  // Si se proporciona zona horaria del usuario, validarla
  if (userTimezone) {
    const isValid = await isValidTimezone(userTimezone);
    if (isValid) {
      userTimezoneCache = userTimezone;
      return userTimezone;
    } else {
      console.warn('⚠️ User timezone is invalid, falling back to browser:', userTimezone);
    }
  }

  // Fallback a zona horaria del navegador
  const effectiveTimezone = getBrowserTimezone();
  return effectiveTimezone;
};

/**
 * Refrescar cache de zona horaria del usuario (llamar cuando se actualiza el perfil)
 */
export const refreshUserTimezone = () => {
  userTimezoneCache = null;
};

/**
 * Formatear fecha con zona horaria inteligente
 */
export const formatSmartDateTime = async (timestamp, userTimezone = null, options = {}) => {
  if (!timestamp) return '';

  try {
    // Determinar zona horaria efectiva
    const effectiveTimezone = await getUserEffectiveTimezone(userTimezone);

    // Preparar fecha
    let date;
    if (typeof timestamp === 'string') {
      if (!timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
        const utcTimestamp = timestamp.includes('T') ? timestamp + 'Z' : timestamp + 'T00:00:00Z';
        date = new Date(utcTimestamp);
      } else {
        date = new Date(timestamp);
      }
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) {
      console.warn('⚠️ Invalid timestamp format:', timestamp);
      return timestamp.toString();
    }

    // Formatear con zona horaria efectiva
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: effectiveTimezone,
      ...options
    };

    const formatter = new Intl.DateTimeFormat('es-ES', defaultOptions);
    return formatter.format(date);

  } catch (error) {
    console.error('❌ Error formatting timestamp with smart timezone:', error, timestamp);
    return timestamp.toString();
  }
};

/**
 * Versión síncrona que usa cache (para casos donde ya se determinó la zona horaria)
 */
export const formatSmartDateTimeSync = (timestamp, effectiveTimezone = null, options = {}) => {
  if (!timestamp) return '';

  try {
    // Si no se proporciona zona horaria efectiva, usar la del navegador
    const timezone = effectiveTimezone || getBrowserTimezone();

    // Preparar fecha
    let date;
    if (typeof timestamp === 'string') {
      if (!timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
        const utcTimestamp = timestamp.includes('T') ? timestamp + 'Z' : timestamp + 'T00:00:00Z';
        date = new Date(utcTimestamp);
      } else {
        date = new Date(timestamp);
      }
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) {
      console.warn('⚠️ Invalid timestamp format:', timestamp);
      return timestamp.toString();
    }

    // Formatear con zona horaria
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: timezone,
      ...options
    };

    const formatter = new Intl.DateTimeFormat('es-ES', defaultOptions);
    return formatter.format(date);

  } catch (error) {
    console.error('❌ Error formatting timestamp sync:', error, timestamp);
    return timestamp.toString();
  }
};

// ========================================
// UPDATED DATE FORMATTING (replaces old functions)
// ========================================

export const formatDate = (date, formatString = 'MMM dd, yyyy', locale = 'en', userTimezone = null) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(dateObj)) return '';

  const localeMap = { es, en };
  return format(dateObj, formatString, { locale: localeMap[locale] || en });
};

export const formatDateTime = async (date, userTimezone = null, locale = 'en') => {
  return await formatSmartDateTime(date, userTimezone, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatTime = async (date, userTimezone = null) => {
  return await formatSmartDateTime(date, userTimezone, {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });
};

export const formatRelativeTime = (date, locale = 'en') => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(dateObj)) return '';

  const localeMap = { es, en };
  return formatDistanceToNow(dateObj, {
    addSuffix: true,
    locale: localeMap[locale] || en
  });
};

// Alias para compatibilidad (este es el que se usaba en PositionTile)
export const formatLocalDateTime = async (timestamp, userTimezone = null) => {
  return await formatSmartDateTime(timestamp, userTimezone);
};

// ========================================
// REMAINING FORMATTERS (unchanged)
// ========================================

// Number formatting
export const formatNumber = (number, decimals = 0, locale = 'en-US') => {
  if (number === null || number === undefined || isNaN(number)) return '';

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);
};

export const formatCurrency = (amount, currency = 'USD', locale = 'en-US') => {
  if (amount === null || amount === undefined || isNaN(amount)) return '';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
};

export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '';

  return `${formatNumber(value, decimals)}%`;
};

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// ========================================
// SENSOR VALUE FORMATTING WITH FORMULAS
// ========================================

// Función para aplicar fórmulas de conversión de sensores
export const convertSensorValue = (rawValue, metricType, rawData = {}) => {
  if (rawValue === null || rawValue === undefined || isNaN(rawValue)) return null;

  switch (metricType) {
    case 'PSI':
    case 'PRESSURE':
      const kPa = 2.8 * (rawData.rpsi || rawValue) + 87.20 - 87.20;
      const psi = kPa * 0.145038;
      return Math.round(psi);

    case 'TEMP':
    case 'TEMPERATURE':
      return (rawData.rtemp || rawValue) - 55;

    case 'VOLTS':
    case 'VOLTAGE':
      return 0.01 * (rawData.rvolts || rawValue) + 1.22;

    case 'ACC':
    case 'ACCELERATION':
      return rawData.peak || rawValue;

    default:
      return rawValue;
  }
};

// Funciones de formato para sensores con conversiones aplicadas
export const formatPressure = (value, unit = 'PSI', applyFormula = false, rawData = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '--';

  let convertedValue = value;
  if (applyFormula) {
    convertedValue = convertSensorValue(value, 'PSI', rawData);
  }

  if (convertedValue === null || convertedValue === undefined || isNaN(convertedValue)) return '--';

  return `${formatNumber(convertedValue, 1)} ${unit}`;
};

export const formatTemperature = (value, unit = 'C', applyFormula = false, rawData = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '--';

  let convertedValue = value;
  if (applyFormula) {
    convertedValue = convertSensorValue(value, 'TEMP', rawData);
  }

  if (convertedValue === null || convertedValue === undefined || isNaN(convertedValue)) return '--';

  const symbol = unit === 'C' ? '°C' : '°F';
  return `${formatNumber(convertedValue, 1)}${symbol}`;
};

export const formatVoltage = (value, unit = 'V', applyFormula = false, rawData = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '--';

  let convertedValue = value;
  if (applyFormula) {
    convertedValue = convertSensorValue(value, 'VOLTS', rawData);
  }

  if (convertedValue === null || convertedValue === undefined || isNaN(convertedValue)) return '--';

  return `${formatNumber(convertedValue, 2)} ${unit}`;
};

export const formatAcceleration = (value, unit = 'g', applyFormula = false, rawData = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '--';

  let convertedValue = value;
  if (applyFormula) {
    convertedValue = convertSensorValue(value, 'ACC', rawData);
  }

  if (convertedValue === null || convertedValue === undefined || isNaN(convertedValue)) return '--';

  return `${formatNumber(convertedValue, 2)} ${unit}`;
};

// Función universal para formatear métricas de telemetría
export const formatTelemetryMetric = (rawData, metricType) => {
  if (!rawData || typeof rawData !== 'object') return '--';

  switch (metricType) {
    case 'PSI':
    case 'PRESSURE':
      return formatPressure(rawData.rpsi, 'PSI', true, rawData);

    case 'TEMP':
    case 'TEMPERATURE':
      return formatTemperature(rawData.rtemp, 'C', true, rawData);

    case 'VOLTS':
    case 'VOLTAGE':
      return formatVoltage(rawData.rvolts, 'V', true, rawData);

    case 'ACC':
    case 'ACCELERATION':
      return formatAcceleration(rawData.peak, 'g', true, rawData);

    default:
      return '--';
  }
};

// Función para procesar datos de telemetría en lote
export const processTelemetryData = (dataArray) => {
  if (!Array.isArray(dataArray)) return [];

  return dataArray.map(item => {
    const processed = { ...item };

    if (item.type === 'PSI') {
      if (item.rpsi !== null && item.rpsi !== undefined) {
        processed.pressure_converted = convertSensorValue(item.rpsi, 'PSI', item);
        processed.pressure_formatted = formatPressure(item.rpsi, 'PSI', true, item);
      }

      if (item.rtemp !== null && item.rtemp !== undefined) {
        processed.temperature_converted = convertSensorValue(item.rtemp, 'TEMP', item);
        processed.temperature_formatted = formatTemperature(item.rtemp, 'C', true, item);
      }

      if (item.rvolts !== null && item.rvolts !== undefined) {
        processed.voltage_converted = convertSensorValue(item.rvolts, 'VOLTS', item);
        processed.voltage_formatted = formatVoltage(item.rvolts, 'V', true, item);
      }
    } else if (item.type === 'ACC') {
      if (item.peak !== null && item.peak !== undefined) {
        processed.acceleration_converted = convertSensorValue(item.peak, 'ACC', item);
        processed.acceleration_formatted = formatAcceleration(item.peak, 'g', true, item);
      }
    }

    return processed;
  });
};

// Text formatting
export const capitalize = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const capitalizeWords = (text) => {
  if (!text) return '';
  return text.split(' ').map(word => capitalize(word)).join(' ');
};

export const truncate = (text, maxLength = 50, suffix = '...') => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - suffix.length) + suffix;
};

export const slugify = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Phone number formatting
export const formatPhoneNumber = (phoneNumber, countryCode = 'US') => {
  if (!phoneNumber) return '';

  const cleaned = phoneNumber.replace(/\D/g, '');

  if (countryCode === 'US' && cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (countryCode === 'MX' && cleaned.length === 10) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }

  return phoneNumber;
};

// RFC formatting (Mexican tax ID)
export const formatRFC = (rfc) => {
  if (!rfc) return '';

  const cleaned = rfc.replace(/\s/g, '').toUpperCase();

  if (cleaned.length === 13) {
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 10)} ${cleaned.slice(10)}`;
  } else if (cleaned.length === 12) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 9)} ${cleaned.slice(9)}`;
  }

  return rfc;
};

// MAC address formatting
export const formatMACAddress = (mac) => {
  if (!mac) return '';

  const cleaned = mac.replace(/[^a-fA-F0-9]/g, '');
  if (cleaned.length !== 12) return mac;

  return cleaned.match(/.{2}/g).join(':').toUpperCase();
};

// Vehicle identification formatting
export const formatVehicleId = (id) => {
  if (!id) return '';
  return id.toString().padStart(4, '0');
};

export const formatLicensePlate = (plate) => {
  if (!plate) return '';
  return plate.toUpperCase().replace(/[^A-Z0-9]/g, '');
};

// Status formatting
export const formatStatus = (status) => {
  if (!status) return '';
  return status.split('_').map(word => capitalize(word)).join(' ');
};

// Array formatting
export const formatList = (items, separator = ', ', lastSeparator = ' and ') => {
  if (!Array.isArray(items) || items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return items.join(lastSeparator);

  return items.slice(0, -1).join(separator) + lastSeparator + items[items.length - 1];
};

// Duration formatting
export const formatDuration = (milliseconds) => {
  if (!milliseconds) return '0s';

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

// Vehicle configuration formatting
export const formatVehicleConfiguration = (config) => {
  if (!config) return '';

  const { positions, figure, configurationP } = config;
  const axleCount = figure ? figure.split(',').length : 0;

  return `${configurationP} (${positions} positions, ${axleCount} axles)`;
};

// Color utilities
export const getStatusColor = (status) => {
  const statusColors = {
    online: '#4caf50',
    offline: '#757575',
    warning: '#ff9800',
    critical: '#f44336',
    error: '#f44336',
    success: '#4caf50',
    info: '#2196f3',
    default: '#757575',
  };

  return statusColors[status?.toLowerCase()] || statusColors.default;
};

// Chart data formatting
export const formatChartData = (data, xKey, yKey, labelKey) => {
  if (!Array.isArray(data)) return [];

  return data.map(item => ({
    x: item[xKey],
    y: item[yKey],
    label: labelKey ? item[labelKey] : item[xKey],
  }));
};

// Export all formatters
export default {
  // NEW: Smart timezone functions
  formatSmartDateTime,
  formatSmartDateTimeSync,
  getUserEffectiveTimezone,
  refreshUserTimezone,
  formatLocalDateTime,

  // Updated date functions
  formatDate,
  formatDateTime,
  formatTime,
  formatRelativeTime,

  // Number functions
  formatNumber,
  formatCurrency,
  formatPercentage,
  formatBytes,

  // Sensor functions
  formatPressure,
  formatTemperature,
  formatAcceleration,
  formatVoltage,
  formatTelemetryMetric,
  processTelemetryData,
  convertSensorValue,

  // Text functions
  capitalize,
  capitalizeWords,
  truncate,
  slugify,

  // Specialized formatting
  formatPhoneNumber,
  formatRFC,
  formatMACAddress,
  formatVehicleId,
  formatLicensePlate,
  formatStatus,
  formatList,
  formatDuration,
  formatVehicleConfiguration,

  // Utilities
  getStatusColor,
  formatChartData,
};